import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from '@respectly/ui';
import { CHAT_BRAND_ID_PATTERN } from '@respectly/shared-types';
import { useChatWidgetsStore } from '../../stores/useChatWidgetsStore';

/**
 * Create a widget.
 *
 * Only an id and a name: a new widget starts inactive with nothing selected, and
 * the page it lands on is where its knowledge, greeting and origins get chosen.
 * The id goes in the embed snippet and is immutable afterwards, which is the one
 * thing worth being careful about here.
 */
export function CreateWidgetDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { create, saving, brands } = useChatWidgetsStore();
  const [brandId, setBrandId] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [error, setError] = useState<string | null>(null);

  const normalized = brandId.trim().toLowerCase();
  const idValid = CHAT_BRAND_ID_PATTERN.test(normalized);
  const idTaken = brands.some((b) => b.brandId === normalized);
  const canSubmit = idValid && !idTaken && displayName.trim().length > 0 && !saving;

  const reset = () => {
    setBrandId('');
    setDisplayName('');
    setError(null);
  };

  const submit = async () => {
    if (!canSubmit) return;
    setError(null);
    const created = await create({
      brandId: normalized,
      displayName: displayName.trim(),
      // Inactive until configured. A widget that goes live the moment it is
      // named would serve an empty greeting and no knowledge to whoever embeds it.
      isActive: false,
    });
    if (created) {
      reset();
      onOpenChange(false);
    } else {
      setError(useChatWidgetsStore.getState().error ?? 'Could not create the widget.');
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
    >
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>New chat widget</DialogTitle>
          <DialogDescription>
            One record per site that embeds the assistant. It starts inactive and knows nothing —
            choose its documents next.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="widget-name">Name</Label>
            <Input
              id="widget-name"
              value={displayName}
              maxLength={120}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Respectly"
            />
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Shown in the widget header and in this list.
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="widget-id">Widget id</Label>
            <Input
              id="widget-id"
              value={brandId}
              maxLength={63}
              onChange={(e) => setBrandId(e.target.value)}
              placeholder="respectly-ai"
              className="font-mono text-sm"
            />
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Goes in the embed snippet as{' '}
              <span className="font-mono">&lt;respectly-chat brand=&quot;…&quot;&gt;</span>.{' '}
              <strong>Permanent</strong> — changing it later would break every page that embeds it.
            </p>
            {normalized && !idValid && (
              <p className="text-xs text-red-600 dark:text-red-400">
                Use 2–63 lowercase letters, numbers and hyphens, starting with a letter or number.
              </p>
            )}
            {idTaken && (
              <p className="text-xs text-red-600 dark:text-red-400">
                A widget with that id already exists.
              </p>
            )}
          </div>

          {error && (
            <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-950/30 dark:text-red-300">
              {error}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={() => void submit()} disabled={!canSubmit}>
            {saving ? 'Creating…' : 'Create widget'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
